import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { addBusinessDays, format } from "date-fns";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CompletionSizingCheckModal } from "@/components/quote/CompletionSizingCheckModal";
import {
  Search,
  Trash2,
  Edit,
  FolderOpen,
  Calendar,
  CalendarCheck,
  User,
  Mail,
  Phone,
  Package,
  RefreshCw,
  Copy,
  Filter,
  LayoutGrid,
  List,
  GripVertical,
  ClipboardList,
  StickyNote,
  Clock,
  AlertTriangle,
  Circle,
  CheckCircle2,
  Timer,
  Archive,
  ArchiveRestore,
  ListTodo,
  Plus,
  DollarSign,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import MainNav from "@/components/MainNav";
import WorkspaceLayout from "@/components/layout/WorkspaceLayout";
import { TaskFinancials, emptyTimeDraft } from "@/components/jobs/TaskFinancials";
import { TaskCustomerField, TaskJobLinkField } from "@/components/jobs/TaskLinks";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Production board columns - Draft excluded from production workflow
const PRODUCTION_COLUMNS = [
  { id: "sent", title: "Sent / Awaiting Response", statuses: ["Sent", "Changes Requested"] },
  { id: "awaiting", title: "Approved & Ready", statuses: ["Approved"] },
  { id: "wip", title: "Work in Progress", statuses: ["In Production", "Work in Progress"] },
  { id: "completed", title: "Completed", statuses: ["Complete"] },
];

// Draft column - visually distinct, no production indicators
const DRAFT_COLUMN = {
  id: "draft",
  title: "Drafts",
  statuses: ["Draft"],
  isDraftColumn: true,
};

// Initial Contact column - pre-quote holding zone for new enquiries
const INITIAL_CONTACT_COLUMN = {
  id: "initial-contact",
  title: "Initial Contact",
  statuses: ["Initial Contact"],
};

// Board view filter options
const BOARD_FILTERS = [
  { value: "active", label: "Active Only" },
  { value: "all", label: "Show Drafts" },
  { value: "drafts", label: "Drafts Only" },
];

// Status filter options for list view (mirror the board columns)
const JOB_STATUS_FILTERS = [
  { value: "All", label: "All Jobs" },
  { value: "Draft", label: "Drafts" },
  { value: "Sent", label: "Sent / Awaiting Response" },
  { value: "Approved", label: "Approved & Ready" },
  { value: "In Production", label: "Work in Progress" },
  { value: "Complete", label: "Completed" },
  { value: "Cancelled", label: "Cancelled" },
];

const JOB_STATUSES = [
  "All",
  "Draft",
  "Sent",
  "Approved",
  "In Production",
  "Complete",
  "Cancelled",
];

const STATUS_COLORS = {
  "Initial Contact": "bg-sky-100 text-sky-700 border-sky-300",
  Draft: "bg-slate-100 text-slate-700 border-slate-300",
  Sent: "bg-blue-100 text-blue-700 border-blue-300",
  Approved: "bg-green-100 text-green-700 border-green-300",
  "In Production": "bg-amber-100 text-amber-700 border-amber-300",
  Complete: "bg-emerald-100 text-emerald-700 border-emerald-300",
  Cancelled: "bg-red-100 text-red-700 border-red-300",
};

// Method badge mapping
const METHOD_BADGES = {
  "UltraColour Max": { label: "DTF", color: "bg-purple-100 text-purple-700" },
  "UltraColour Pro": { label: "HYBRID", color: "bg-indigo-100 text-indigo-700" },
  "Screen Print Pro": { label: "SCREEN", color: "bg-orange-100 text-orange-700" },
  "Embroidery": { label: "EMB", color: "bg-teal-100 text-teal-700" },
  "Manual Override": { label: "CUSTOM", color: "bg-gray-100 text-gray-700" },
  "DTG": { label: "DTG", color: "bg-cyan-100 text-cyan-700" },
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  try {
    return new Date(dateStr).toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
    });
  } catch {
    return dateStr;
  }
};

// Calculate deadline countdown
const getDeadlineCountdown = (deadline) => {
  if (!deadline) return null;
  
  const now = new Date();
  const deadlineDate = new Date(deadline + "T23:59:59"); // End of deadline day
  const diffMs = deadlineDate - now;
  
  if (diffMs < 0) {
    const overdueDays = Math.ceil(Math.abs(diffMs) / (1000 * 60 * 60 * 24));
    return { overdue: true, days: overdueDays, text: `${overdueDays}d overdue` };
  }
  
  const totalHours = Math.floor(diffMs / (1000 * 60 * 60));
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  
  if (days === 0) {
    return { overdue: false, days: 0, hours, text: hours <= 1 ? "Today" : `${hours}h` };
  } else if (days === 1) {
    return { overdue: false, days: 1, text: "Tomorrow" };
  } else {
    return { overdue: false, days, text: `${days}d` };
  }
};

// "Last Contacted" elapsed time since a card was created or last updated
const getLastContacted = (job) => {
  const ts = job.updated_at || job.created_at;
  if (!ts) return null;
  const diffMs = Date.now() - new Date(ts).getTime();
  if (diffMs < 0) return { text: "Just now", days: 0, stale: false };
  const mins = Math.floor(diffMs / 60000);
  const hours = Math.floor(mins / 60);
  const days = Math.floor(hours / 24);
  let text;
  if (mins < 1) text = "Just now";
  else if (hours < 1) text = `${mins}m ago`;
  else if (days < 1) text = `${hours}h ago`;
  else text = `${days}d ago`;
  return { text, days, stale: days >= 3 };
};

// Get unique production methods from a job's artwork groups
const getProductionMethods = (job) => {
  const methods = new Set();
  const artworkGroups = job.job_data?.artwork_groups || [];
  artworkGroups.forEach((ag) => {
    if (ag.print_method && METHOD_BADGES[ag.print_method]) {
      methods.add(ag.print_method);
    }
  });
  return Array.from(methods);
};

// Calculate total from job data
const getJobTotal = (job) => {
  if (job.job_data?.client_type === "Retail") {
    if (job.job_data?.retail_total) {
      return job.job_data.retail_total;
    }
    if (job.job_data?.total_inc_gst) {
      return job.job_data.total_inc_gst;
    }
  }
  const snapshots = job.snapshots || [];
  if (snapshots.length > 0) {
    const latest = snapshots[snapshots.length - 1];
    return latest.total_inc_gst || 0;
  }
  return job.job_data?.total_inc_gst || 0;
};

// Calculate countdown for retail jobs
const getRetailCountdown = (job) => {
  if (job.job_data?.client_type !== "Retail" || !job.job_data?.expected_production_time) {
    return null;
  }
  
  const createdAt = new Date(job.created_at);
  const expectedMinutes = job.job_data.expected_production_time;
  const dueTime = new Date(createdAt.getTime() + expectedMinutes * 60000);
  const now = new Date();
  const diffMinutes = Math.floor((dueTime - now) / 60000);
  
  return {
    minutes: diffMinutes,
    isOverdue: diffMinutes < 0,
    display: diffMinutes < 0 
      ? "OVERDUE" 
      : diffMinutes < 60 
        ? `${diffMinutes} min`
        : `${Math.floor(diffMinutes / 60)}h ${diffMinutes % 60}m`
  };
};

// Job Card Component for Board View
function JobCard({ job, onDragStart, onDragEnd, onClick, onTaskClick, onDuplicate, onProductionSheet, onProductionStatusChange, onArchive, showArchiveButton = false, defaultTaskRate }) {
  const methods = getProductionMethods(job);
  const total = getJobTotal(job);
  const garmentCount = job.job_data?.garments?.reduce((sum, g) => sum + (g.quantity || 0), 0) || 0;
  const isRetail = job.job_data?.client_type === "Retail";
  const isTask = job.job_data?.job_type === "task";
  const countdown = getRetailCountdown(job);
  const lastContacted = job.status === "Initial Contact" ? getLastContacted(job) : null;
  
  // Deadline countdown for WIP jobs
  const deadline = job.deadline || job.job_data?.deadline;
  const deadlineCountdown = (job.status === "In Production" || job.status === "Approved") && deadline
    ? getDeadlineCountdown(deadline)
    : null;
  
  // Production status from job data or top-level
  const garmentsOrdered = job.garments_ordered ?? job.job_data?.garments_ordered ?? false;
  const transfersOrdered = job.transfers_ordered ?? job.job_data?.transfers_ordered ?? false;
  const artworkApproved = job.artwork_approved ?? job.job_data?.artwork_approved ?? false;
  const invoicePaid = job.invoice_paid ?? job.job_data?.invoice_paid ?? false;
  const allPrepped = garmentsOrdered && transfersOrdered && artworkApproved;

  const handleDuplicate = (e) => {
    e.stopPropagation();
    onDuplicate(job.id);
  };

  const handleProductionSheet = (e) => {
    e.stopPropagation();
    onProductionSheet(job);
  };

  const handleArchiveClick = (e) => {
    e.stopPropagation();
    onArchive?.(job.id);
  };
  
  const handleStatusToggle = (e, field, currentValue) => {
    e.stopPropagation();
    onProductionStatusChange(job.id, field, !currentValue);
  };

  const canGenerateSheet = job.status !== "Draft";
  const showProductionStatus = job.status === "Approved" || job.status === "In Production" || job.status === "Complete";

  // Non-print task cards render a stripped-down card (no pricing/methods/toggles)
  if (isTask) {
    const taskNotes = job.job_data?.notes || "";
    const timeLog = job.job_data?.time_log || [];
    const loggedHours = timeLog.reduce((s, e) => s + (+e.hours || 0), 0);
    const taskRate = job.job_data?.task_hourly_rate ?? defaultTaskRate ?? 0;
    return (
      <div
        draggable
        onDragStart={(e) => onDragStart(e, job)}
        onDragEnd={onDragEnd}
        onClick={() => onTaskClick(job)}
        data-testid={`board-card-${job.id}`}
        className="cursor-pointer mb-3 p-4 rounded-aa-lg transition-all hover:-translate-y-0.5 bg-indigo-50 border-l-4 border-indigo-400 shadow-aa-card hover:shadow-aa-card-hover"
      >
        <div className="flex items-start gap-2">
          <GripVertical className="w-4 h-4 text-gray-300 mt-1 flex-shrink-0 cursor-grab" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="px-2 py-0.5 rounded-aa-sm text-aa-xs font-semibold bg-indigo-200 text-indigo-800 flex items-center gap-1" data-testid="task-badge">
                <ListTodo className="w-3 h-3" />
                TASK
              </span>
              <h4 className="font-medium text-aa-sm text-gray-900 truncate flex-1" title={job.job_name} data-testid="job-card-title">
                {job.job_name || "Untitled Task"}
              </h4>
            </div>
            <div className="flex items-center gap-3 text-aa-xs text-gray-500 mb-1 flex-wrap">
              {job.client_name && (
                <span className="flex items-center gap-1 truncate">
                  <User className="w-3 h-3" />
                  {job.client_name}
                </span>
              )}
              {deadline && (
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  Due {formatDate(deadline)}
                </span>
              )}
            </div>
            {taskNotes && (
              <p className="text-aa-xs text-gray-600 whitespace-pre-wrap mt-1 line-clamp-3">{taskNotes}</p>
            )}
            {timeLog.length > 0 && (
              <div className="flex items-center gap-1 mt-1.5 text-aa-xs font-medium text-indigo-700" data-testid={`task-card-time-${job.id}`}>
                <Clock className="w-3 h-3" />
                {Math.round(loggedHours * 100) / 100}h / {new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(loggedHours * taskRate)}
              </div>
            )}
            <div className="flex items-center justify-end mt-2">
              {showArchiveButton && (
                <button
                  onClick={(e) => { e.stopPropagation(); onArchive?.(job.id); }}
                  className="h-6 px-2 text-aa-xs text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-aa-sm transition-all"
                  title="Archive Now"
                  data-testid={`archive-card-${job.id}`}
                >
                  <Archive className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, job)}
      onDragEnd={onDragEnd}
      onClick={() => onClick(job.id)}
      data-testid={`board-card-${job.id}`}
      className={`cursor-pointer mb-3 p-4 rounded-aa-lg transition-all hover:-translate-y-0.5 ${
        isRetail 
          ? "bg-aa-retail-bg border-l-4 border-aa-retail shadow-aa-card hover:shadow-aa-card-hover" 
          : allPrepped && showProductionStatus
            ? "bg-aa-success/5 border-l-4 border-aa-success shadow-aa-card hover:shadow-aa-card-hover"
            : "bg-aa-card shadow-aa-card hover:shadow-aa-card-hover"
      }`}
    >
      <div className="flex items-start gap-2">
        <GripVertical className="w-4 h-4 text-gray-300 mt-1 flex-shrink-0 cursor-grab" />
        <div className="flex-1 min-w-0">
          {/* Job number and name */}
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            {job.job_number && (
              <span className="px-1.5 py-0.5 bg-white rounded-aa-sm text-aa-xs font-mono text-gray-600 shadow-sm">
                {job.job_number}
              </span>
            )}
            <h4 className="font-medium text-aa-sm text-gray-900 truncate flex-1" title={job.job_name} data-testid="job-card-title">
              {job.job_name || "Untitled"}
            </h4>
          </div>
          
          {/* Status badges */}
          <div className="flex items-center gap-1.5 mb-2 flex-wrap">
            <span className={`px-2 py-0.5 rounded-aa-sm text-aa-xs font-medium ${STATUS_COLORS[job.status] || STATUS_COLORS.Draft}`}>
              {job.status || "Draft"}
            </span>
            {isRetail && (
              <span className="px-2 py-0.5 rounded-aa-sm text-aa-xs font-medium bg-aa-retail/10 text-aa-retail">
                Retail
              </span>
            )}
            {/* Quantity Break Badge */}
            {job.job_data?.is_quantity_break && (
              <span className="px-2 py-0.5 rounded-aa-sm text-aa-xs font-medium bg-purple-100 text-purple-700" data-testid="qty-break-badge">
                QTY BREAK
              </span>
            )}
            {/* BYO Badge - show if any garments are BYO */}
            {job.job_data?.garments?.some(g => g.is_byo || g.product_id === "byo-garment-system") && (
              <span className="px-2 py-0.5 rounded-aa-sm text-aa-xs font-medium bg-purple-100 text-purple-700">
                BYO
              </span>
            )}
            {allPrepped && showProductionStatus && (
              <span className="px-2 py-0.5 rounded-aa-sm text-aa-xs font-medium bg-aa-success/10 text-aa-success flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" />
                Ready
              </span>
            )}
            {invoicePaid && (
              <span className="px-2 py-0.5 rounded-aa-sm text-aa-xs font-medium bg-emerald-600 text-white flex items-center gap-1" data-testid={`paid-badge-${job.id}`}>
                <DollarSign className="w-3 h-3" />
                Paid
              </span>
            )}
            {job.job_data?.retail_design_notes && (
              <span 
                className="px-2 py-0.5 rounded-aa-sm text-aa-xs font-medium bg-amber-100 text-amber-700 flex items-center gap-0.5"
                title={job.job_data.retail_design_notes}
              >
                <StickyNote className="w-2.5 h-2.5" />
                Notes
              </span>
            )}
          </div>
          
          {/* Client and date */}
          <div className="flex items-center gap-3 text-aa-xs text-gray-500 mb-2 flex-wrap">
            {job.client_name && (
              <span className="flex items-center gap-1 truncate">
                <User className="w-3 h-3" />
                {job.client_name}
              </span>
            )}
            {job.quote_date && (
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {formatDate(job.quote_date)}
              </span>
            )}
            {(job.due_date || job.job_data?.due_date) && (
              <span
                className="flex items-center gap-1 font-medium text-aa-primary"
                data-testid={`job-card-due-date-${job.id}`}
                title="Order due date"
              >
                <CalendarCheck className="w-3 h-3" />
                Due {formatDate(job.due_date || job.job_data?.due_date)}
              </span>
            )}
          </div>
          
          {/* Last Contacted timer for Initial Contact enquiries */}
          {lastContacted && (
            <div
              className={`flex items-center gap-1 text-aa-xs px-2 py-1.5 rounded-aa-sm mb-2 ${
                lastContacted.stale ? "bg-amber-100 text-amber-700" : "bg-sky-50 text-sky-700"
              }`}
              data-testid={`last-contacted-${job.id}`}
              title="Time since this enquiry was created or last updated"
            >
              <Clock className="w-3 h-3" />
              <span className="font-medium">Last contacted {lastContacted.text}</span>
            </div>
          )}

          {/* Countdown timer for retail jobs */}
          {countdown && job.status === "In Production" && (
            <div className={`flex items-center gap-1 text-aa-xs px-2 py-1.5 rounded-aa-sm mb-2 ${
              countdown.isOverdue 
                ? "bg-aa-error/10 text-aa-error" 
                : "bg-aa-success/10 text-aa-success"
            }`}>
              {countdown.isOverdue ? (
                <AlertTriangle className="w-3 h-3" />
              ) : (
                <Clock className="w-3 h-3" />
              )}
              <span className="font-medium">
                {countdown.isOverdue ? "OVERDUE" : `Ready in: ${countdown.display}`}
              </span>
            </div>
          )}
          
          {/* Deadline countdown timer */}
          {deadlineCountdown && !countdown && (
            <div className={`flex items-center gap-1 text-aa-xs px-2 py-1.5 rounded-aa-sm mb-2 ${
              deadlineCountdown.overdue 
                ? "bg-aa-error/10 text-aa-error" 
                : deadlineCountdown.days <= 1
                  ? "bg-amber-100 text-amber-700"
                  : "bg-blue-100 text-blue-700"
            }`}>
              <Timer className="w-3 h-3" />
              <span className="font-medium">
                {deadlineCountdown.overdue ? `${deadlineCountdown.text}` : `Due: ${deadlineCountdown.text}`}
              </span>
            </div>
          )}
          
          {/* Production Status Toggles */}
          {showProductionStatus && (
            <div className="flex flex-col gap-1 mb-2 p-2 bg-aa-bg rounded-aa-sm">
              <button
                onClick={(e) => handleStatusToggle(e, 'garments_ordered', garmentsOrdered)}
                className={`flex items-center gap-2 text-aa-xs transition-colors p-1 rounded hover:bg-white ${
                  garmentsOrdered ? 'text-aa-success' : 'text-gray-400 hover:text-gray-600'
                }`}
                data-testid={`toggle-garments-${job.id}`}
              >
                {garmentsOrdered ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Circle className="w-3.5 h-3.5" />}
                <span className={garmentsOrdered ? 'font-medium' : ''}>Garments Ordered</span>
              </button>
              <button
                onClick={(e) => handleStatusToggle(e, 'transfers_ordered', transfersOrdered)}
                className={`flex items-center gap-2 text-aa-xs transition-colors p-1 rounded hover:bg-white ${
                  transfersOrdered ? 'text-aa-success' : 'text-gray-400 hover:text-gray-600'
                }`}
                data-testid={`toggle-transfers-${job.id}`}
              >
                {transfersOrdered ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Circle className="w-3.5 h-3.5" />}
                <span className={transfersOrdered ? 'font-medium' : ''}>Transfers Ordered</span>
              </button>
              <button
                onClick={(e) => handleStatusToggle(e, 'artwork_approved', artworkApproved)}
                className={`flex items-center gap-2 text-aa-xs transition-colors p-1 rounded hover:bg-white ${
                  artworkApproved ? 'text-aa-success' : 'text-gray-400 hover:text-gray-600'
                }`}
                data-testid={`toggle-artwork-${job.id}`}
              >
                {artworkApproved ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Circle className="w-3.5 h-3.5" />}
                <span className={artworkApproved ? 'font-medium' : ''}>Artwork Approved</span>
              </button>
              <button
                onClick={(e) => handleStatusToggle(e, 'invoice_paid', invoicePaid)}
                className={`flex items-center gap-2 text-aa-xs transition-colors p-1 rounded hover:bg-white border-t border-gray-200 mt-0.5 pt-1.5 ${
                  invoicePaid ? 'text-emerald-600' : 'text-gray-400 hover:text-gray-600'
                }`}
                data-testid={`toggle-invoice-paid-${job.id}`}
                title="Only Invoice Paid jobs count toward revenue analytics"
              >
                {invoicePaid ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Circle className="w-3.5 h-3.5" />}
                <span className={invoicePaid ? 'font-medium' : ''}>Invoice Paid</span>
              </button>
            </div>
          )}
          
          {/* Total and action buttons */}
          <div className="flex items-center justify-between mb-2">
            <div className="text-aa-sm font-semibold text-gray-900">
              {formatCurrency(total)}
              {garmentCount > 0 && (
                <span className="text-aa-xs font-normal text-gray-500 ml-2">
                  ({garmentCount} units)
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {canGenerateSheet && (
                  <button
                    onClick={handleProductionSheet}
                    className="h-6 px-2 text-aa-xs text-aa-retail hover:bg-aa-retail/10 rounded-aa-sm transition-all"
                    title="Production Sheet"
                    data-testid={`production-sheet-card-${job.id}`}
                  >
                    <ClipboardList className="w-3 h-3" />
                  </button>
                )}
                <button
                  onClick={handleDuplicate}
                  className="h-6 px-2 text-aa-xs text-aa-primary hover:bg-aa-primary/10 rounded-aa-sm transition-all flex items-center gap-1"
                  title="Duplicate Job"
                  data-testid={`duplicate-board-card-${job.id}`}
                >
                  <Copy className="w-3 h-3" />
                  Duplicate
                </button>
                {showArchiveButton && (
                  <button
                    onClick={handleArchiveClick}
                    className="h-6 px-2 text-aa-xs text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-aa-sm transition-all flex items-center gap-1"
                    title="Archive Now"
                    data-testid={`archive-card-${job.id}`}
                  >
                    <Archive className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
            
            {/* Production method badges */}
            {methods.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {methods.map((method) => (
                  <span 
                    key={method} 
                    className={`px-2 py-0.5 rounded-aa-sm text-aa-xs font-medium ${METHOD_BADGES[method]?.color || "bg-gray-100 text-gray-600"}`}
                  >
                    {METHOD_BADGES[method]?.label || method}
                  </span>
                ))}
              </div>
            )}
        </div>
      </div>
    </div>
  );
}

// Board Column Component
function BoardColumn({ column, jobs, onDragOver, onDrop, onDragStart, onDragEnd, onCardClick, onTaskClick, onDuplicate, onProductionSheet, onProductionStatusChange, onArchive, isOver, onAddEnquiry, defaultTaskRate }) {
  const count = jobs.length;
  const isCompletedColumn = column.id === "completed";

  return (
    <div 
      className={`flex-shrink-0 w-72 bg-aa-bg rounded-aa-lg p-4 transition-all flex flex-col h-full ${
        isOver ? "ring-2 ring-aa-primary ring-offset-2" : ""
      }`}
      onDragOver={onDragOver}
      onDrop={(e) => onDrop(e, column)}
      data-testid={`board-column-${column.id}`}
    >
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <h3 className="font-semibold text-aa-sm text-gray-700">{column.title}</h3>
        <span className="px-2 py-1 bg-aa-card rounded-aa-sm text-aa-xs font-medium text-gray-600 shadow-sm" data-testid={`column-count-${column.id}`}>
          {count}
        </span>
      </div>

      {onAddEnquiry && (
        <button
          onClick={onAddEnquiry}
          data-testid="add-enquiry-btn"
          className="flex-shrink-0 mb-3 w-full flex items-center justify-center gap-1.5 py-2 rounded-aa-md border border-dashed border-sky-300 bg-sky-50 text-sky-700 text-aa-xs font-medium hover:bg-sky-100 hover:border-sky-400 transition-all"
        >
          <Plus className="w-3.5 h-3.5" />
          New Enquiry
        </button>
      )}
      
      <div className="flex-1 overflow-y-auto min-h-0 -mr-2 pr-2">
        {jobs.length === 0 ? (
          <div className="text-center py-8 text-aa-xs text-gray-400">
            {isCompletedColumn ? (
              <div className="flex flex-col items-center gap-2">
                <CheckCircle2 className="w-8 h-8 text-gray-300" />
                <p>No completed jobs</p>
                <p className="text-[10px] text-gray-300">Completed jobs will appear here</p>
              </div>
            ) : (
              "No jobs"
            )}
          </div>
        ) : (
          jobs.map((job) => (
            <JobCard
              key={job.id}
              job={job}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              onClick={onCardClick}
              onTaskClick={onTaskClick}
              onDuplicate={onDuplicate}
              onProductionSheet={onProductionSheet}
              onProductionStatusChange={onProductionStatusChange}
              onArchive={onArchive}
              showArchiveButton={isCompletedColumn}
              defaultTaskRate={defaultTaskRate}
            />
          ))
        )}
      </div>
    </div>
  );
}

// List View Row Component
function JobListRow({ job, onEdit, onDuplicate, onDelete }) {
  const isRetail = job.job_data?.client_type === "Retail";
  
  return (
    <Card
      className={`hover:border-primary/50 transition-colors cursor-pointer ${
        isRetail ? "bg-orange-50 border-orange-200" : ""
      }`}
      onClick={() => onEdit(job.id)}
      data-testid={`job-card-${job.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              {job.job_number && (
                <Badge variant="outline" className="font-mono text-xs">
                  {job.job_number}
                </Badge>
              )}
              <h3 className="font-semibold text-lg">
                {job.job_name || "Untitled Job"}
              </h3>
              <Badge 
                variant="outline" 
                className={`text-xs ${STATUS_COLORS[job.status] || STATUS_COLORS.Draft}`}
              >
                {job.status || "Draft"}
              </Badge>
              {isRetail && (
                <Badge variant="outline" className="text-xs bg-orange-100 text-orange-700 border-orange-300">
                  Retail
                </Badge>
              )}
              {job.job_data?.is_quantity_break && (
                <Badge variant="outline" className="text-xs bg-purple-100 text-purple-700 border-purple-300">
                  QTY BREAK
                </Badge>
              )}
            </div>
            
            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
              {job.quote_date && (
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {formatDate(job.quote_date)}
                </span>
              )}
              {job.client_name && (
                <span className="flex items-center gap-1">
                  <User className="w-3 h-3" />
                  {job.client_name}
                </span>
              )}
              {job.client_email && (
                <span className="flex items-center gap-1">
                  <Mail className="w-3 h-3" />
                  {job.client_email}
                </span>
              )}
              {job.client_phone && (
                <span className="flex items-center gap-1">
                  <Phone className="w-3 h-3" />
                  {job.client_phone}
                </span>
              )}
            </div>
            
            {job.job_data?.garments?.length > 0 && (
              <div className="mt-2 text-xs text-muted-foreground">
                {job.job_data.garments.length} garment{job.job_data.garments.length !== 1 ? "s" : ""} • 
                {" "}{job.job_data.garments.reduce((sum, g) => sum + (g.quantity || 0), 0)} total units
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-2 ml-4">
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onDuplicate(job.id);
              }}
              data-testid={`duplicate-job-${job.id}`}
              className="text-blue-600 border-blue-300 hover:bg-blue-50"
            >
              <Copy className="w-4 h-4 mr-1" />
              Duplicate
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(job.id);
              }}
              data-testid={`edit-job-${job.id}`}
            >
              <Edit className="w-4 h-4 mr-1" />
              Edit
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(job.id);
              }}
              data-testid={`delete-job-${job.id}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Draft Job Card - Simplified card for draft column (no production indicators)
function DraftJobCard({ job, onClick }) {
  const clientName = job.client_name || job.job_data?.client?.business_name || job.job_data?.client?.contact_name || "";
  const lastEdited = job.updated_at ? formatDate(job.updated_at) : formatDate(job.created_at);
  
  return (
    <div
      onClick={() => onClick(job.id)}
      className="bg-white rounded-aa-md p-3 cursor-pointer hover:shadow-md transition-all border border-slate-200 hover:border-slate-300"
      data-testid={`draft-card-${job.id}`}
    >
      {/* Job Number & Name */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-aa-xs font-mono text-slate-400">
              {job.job_number}
            </span>
            <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-500 border-slate-200">
              Draft
            </Badge>
            {/* Quantity Break Badge */}
            {job.job_data?.is_quantity_break && (
              <Badge variant="outline" className="text-[10px] bg-purple-100 text-purple-700 border-purple-300" data-testid="qty-break-badge">
                QTY BREAK
              </Badge>
            )}
          </div>
          <h4 className="text-aa-sm font-medium text-slate-700 truncate mt-0.5">
            {job.job_name || "Untitled Quote"}
          </h4>
        </div>
      </div>
      
      {/* Customer */}
      {clientName && (
        <div className="flex items-center gap-1.5 text-aa-xs text-slate-500 mb-2">
          <User className="w-3 h-3" />
          <span className="truncate">{clientName}</span>
        </div>
      )}
      
      {/* Last Edited */}
      <div className="flex items-center gap-1.5 text-[10px] text-slate-400">
        <Clock className="w-3 h-3" />
        <span>Edited {lastEdited}</span>
      </div>
    </div>
  );
}

export default function Jobs() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialStatus = searchParams.get("status") || "All";
  const initialView = searchParams.get("view") === "list" ? "list" : "board";
  const [viewMode, setViewMode] = useState(initialView);
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const [methodFilter, setMethodFilter] = useState("All");
  const [boardFilter, setBoardFilter] = useState("all"); // "active" | "all" | "drafts" - Default to "all" to show drafts
  const [clientTypeFilter, setClientTypeFilter] = useState("All"); // "All" | "Retail" | "Business"
  const [taskFilter, setTaskFilter] = useState("all"); // "all" | "hide" | "only"
  const [isLoading, setIsLoading] = useState(false);
  const [draggedJob, setDraggedJob] = useState(null);
  const [overColumn, setOverColumn] = useState(null);
  const [completionCheckJob, setCompletionCheckJob] = useState(null);
  const [statusCounts, setStatusCounts] = useState({});
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [isSavingTask, setIsSavingTask] = useState(false);
  const [taskForm, setTaskForm] = useState({
    job_name: "",
    customer_id: null,
    client: {},
    linked_job: null,
    due_date: "",
    notes: "",
    status: "In Production",
    quoted_amount: "",
    task_hourly_rate: "",
    time_log: [],
  });
  const [taskTimeDraft, setTaskTimeDraft] = useState(emptyTimeDraft());
  const [defaultLabourRate, setDefaultLabourRate] = useState(null);

  const [showEnquiryModal, setShowEnquiryModal] = useState(false);
  const [isSavingEnquiry, setIsSavingEnquiry] = useState(false);
  const [enquiryForm, setEnquiryForm] = useState({
    job_name: "",
    business_name: "",
    contact_name: "",
    email: "",
    phone: "",
    notes: "",
  });

  const openEnquiryCreate = () => {
    setEnquiryForm({ job_name: "", business_name: "", contact_name: "", email: "", phone: "", notes: "" });
    setShowEnquiryModal(true);
  };

  const saveEnquiry = async () => {
    if (!enquiryForm.job_name.trim() && !enquiryForm.business_name.trim() && !enquiryForm.contact_name.trim()) {
      toast.error("Add an enquiry name or a customer name");
      return;
    }
    setIsSavingEnquiry(true);
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const payload = {
        job_name: enquiryForm.job_name.trim() || enquiryForm.business_name.trim() || enquiryForm.contact_name.trim(),
        client_type: "Business",
        margin_percent: 0.35,
        status: "Initial Contact",
        notes: enquiryForm.notes || null,
        client: {
          business_name: enquiryForm.business_name || "",
          contact_name: enquiryForm.contact_name || "",
          email: enquiryForm.email || "",
          phone: enquiryForm.phone || "",
        },
        garments: [],
        artwork_groups: [],
      };
      await axios.post(`${API}/jobs/save`, payload, { headers });
      toast.success("Enquiry logged");
      setShowEnquiryModal(false);
      loadJobs(search, statusFilter);
    } catch (error) {
      toast.error("Failed to log enquiry");
    } finally {
      setIsSavingEnquiry(false);
    }
  };

  const getToken = () => localStorage.getItem("auth_token");

  const loadDefaultLabourRate = async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      setDefaultLabourRate(res.data?.labour_rate ?? null);
    } catch (error) {
      // Non-critical; time log value falls back to $0/hr until a rate is set
    }
  };

  const openTaskCreate = () => {
    setEditingTaskId(null);
    setTaskForm({ job_name: "", customer_id: null, client: {}, linked_job: null, due_date: "", notes: "", status: "In Production", quoted_amount: "", task_hourly_rate: "", time_log: [] });
    setTaskTimeDraft(emptyTimeDraft());
    loadDefaultLabourRate();
    setShowTaskModal(true);
  };

  const openTaskEdit = (job) => {
    setEditingTaskId(job.id);
    const jd = job.job_data || {};
    setTaskForm({
      job_name: job.job_name || "",
      customer_id: job.customer_id || null,
      // Untouched legacy tasks keep their plain-text contact name as-is
      client: jd.client || {},
      linked_job: jd.linked_job || null,
      due_date: job.deadline || job.job_data?.deadline || "",
      notes: job.job_data?.notes || "",
      status: job.status || "In Production",
      quoted_amount: jd.quoted_amount ?? "",
      task_hourly_rate: jd.task_hourly_rate ?? "",
      time_log: jd.time_log || [],
    });
    setTaskTimeDraft(emptyTimeDraft());
    loadDefaultLabourRate();
    setShowTaskModal(true);
  };

  const saveTask = async () => {
    if (!taskForm.job_name.trim()) {
      toast.error("Task name is required");
      return;
    }
    if (taskTimeDraft.hours !== "") {
      toast.error("Click Add to log the time entry, or clear the hours box");
      return;
    }
    setIsSavingTask(true);
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const payload = {
        job_name: taskForm.job_name.trim(),
        client_type: "Business",
        margin_percent: 0,
        job_type: "task",
        status: taskForm.status,
        deadline: taskForm.due_date || null,
        notes: taskForm.notes || null,
        quoted_amount: taskForm.quoted_amount === "" ? null : Math.max(parseFloat(taskForm.quoted_amount) || 0, 0),
        task_hourly_rate: taskForm.task_hourly_rate === "" ? null : Math.max(parseFloat(taskForm.task_hourly_rate) || 0, 0),
        time_log: taskForm.time_log || [],
        linked_job: taskForm.linked_job || null,
        customer_id: taskForm.customer_id || null,
        client: taskForm.client || {},
        garments: [],
        artwork_groups: [],
      };
      if (editingTaskId) {
        await axios.put(`${API}/jobs/${editingTaskId}`, payload, { headers: { ...headers, "X-Status-Source": "task_dialog_save" } });
        toast.success("Task updated");
      } else {
        await axios.post(`${API}/jobs/save`, payload, { headers: { ...headers, "X-Status-Source": "task_dialog_save" } });
        toast.success("Task created");
      }
      setShowTaskModal(false);
      loadJobs(search, statusFilter);
    } catch (error) {
      toast.error("Failed to save task");
    } finally {
      setIsSavingTask(false);
    }
  };

  const loadStatusCounts = async () => {
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/jobs/status-counts`, { headers });
      setStatusCounts(res.data || {});
    } catch (error) {
      // Non-critical; filter labels simply omit counts on failure
    }
  };

  useEffect(() => {
    loadJobs(search, initialStatus);
    loadStatusCounts();
    loadDefaultLabourRate();
    // Clear query param after applying so refresh doesn't re-pin filter
    if (searchParams.get("status") || searchParams.get("view")) {
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadJobs = async (searchQuery = "", status = "") => {
    setIsLoading(true);
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const params = { search: searchQuery, limit: 100 };
      
      // Handle grouped status filters
      if (status && status !== "All") {
        if (status === "Sent") {
          // "Sent / Awaiting Response" board column = Sent + Changes Requested
          params.statuses = "Sent,Changes Requested";
        } else {
          params.status = status;
        }
      }
      
      const res = await axios.get(`${API}/jobs`, { params, headers });
      const allJobs = res.data.jobs || res.data || [];
      setJobs(allJobs);
      setTotal(res.data.total || allJobs.length);
      loadStatusCounts();
    } catch (error) {
      console.error("Failed to load jobs", error);
      toast.error("Failed to load jobs");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadJobs(search, statusFilter);
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    loadJobs(search, status);
  };

  const openJob = (jobId) => {
    // All jobs open in QuoteBuilder - Quantity Break Mode is auto-enabled based on job data
    navigate(`/quote?job=${jobId}`);
  };

  const handleDuplicateJob = async (jobId) => {
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.post(`${API}/jobs/${jobId}/duplicate`, {}, { headers });
      toast.success("Job duplicated successfully");
      navigate(`/quote?job=${res.data.id}`);
    } catch (error) {
      console.error("Failed to duplicate job", error);
      toast.error("Failed to duplicate job");
    }
  };

  const handleDelete = async (jobId) => {
    if (!window.confirm("Are you sure you want to delete this job?")) {
      return;
    }
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API}/jobs/${jobId}`, { headers });
      toast.success("Job deleted");
      loadJobs(search, statusFilter);
    } catch (error) {
      console.error("Failed to delete job", error);
      toast.error("Failed to delete job");
    }
  };

  // Archive a job (manual archive now)
  const handleArchive = async (jobId) => {
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/jobs/${jobId}/archive`, {}, { headers });
      toast.success("Job archived");
      loadJobs(search, statusFilter);
    } catch (error) {
      console.error("Failed to archive job", error);
      toast.error("Failed to archive job");
    }
  };

  // Restore a job from archive
  const handleRestore = async (jobId) => {
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/jobs/${jobId}/restore`, {}, { headers });
      toast.success("Job restored");
      loadArchivedJobs();
      loadJobs(search, statusFilter);
    } catch (error) {
      console.error("Failed to restore job", error);
      toast.error("Failed to restore job");
    }
  };

  // Load archived jobs
  const [archivedJobs, setArchivedJobs] = useState([]);
  const [archivedTotal, setArchivedTotal] = useState(0);
  const [showArchived, setShowArchived] = useState(false);
  const [archivedSearch, setArchivedSearch] = useState("");

  const loadArchivedJobs = async (searchTerm = "") => {
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/jobs/archived`, {
        params: { search: searchTerm, limit: 50 },
        headers,
      });
      setArchivedJobs(res.data.jobs || []);
      setArchivedTotal(res.data.total || 0);
    } catch (error) {
      console.error("Failed to load archived jobs", error);
    }
  };

  // Load archived jobs when archive view is opened
  useEffect(() => {
    if (showArchived) {
      loadArchivedJobs(archivedSearch);
    }
  }, [showArchived]);

  const handleProductionSheet = async (job) => {
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/jobs/${job.id}/production-sheet`, {
        responseType: 'blob',
        headers,
      });
      
      let filename = `ProductionSheet_${job.job_number || 'DRAFT'}.pdf`;
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Downloaded: ${filename}`);
    } catch (error) {
      console.error("Failed to download production sheet:", error);
      toast.error("Failed to generate production sheet");
    }
  };

  // Update production status toggles
  const handleProductionStatusChange = async (jobId, field, value) => {
    // Snapshot the job BEFORE the optimistic update so we can evaluate the auto-move rule
    const jobBefore = jobs.find(j => j.id === jobId);

    // Smart Due Date: ticking "Invoice Paid" sets the Due Date based on the job's
    // lead-time config, counted from today (the invoice-paid date):
    //   - mode "days":  today + N working days
    //   - mode "weeks": today + (N × 5) working days
    //   - mode "date":  leave the manually-chosen specific date untouched
    let smartDueDate = null;
    if (field === "invoice_paid" && value === true) {
      const jd = jobBefore?.job_data || {};
      const mode = jobBefore?.due_date_mode ?? jd.due_date_mode ?? "date";
      const leadValue = Number(jobBefore?.due_date_value ?? jd.due_date_value ?? 0);
      if (mode === "days" && leadValue > 0) {
        smartDueDate = format(addBusinessDays(new Date(), leadValue), "yyyy-MM-dd");
      } else if (mode === "weeks" && leadValue > 0) {
        smartDueDate = format(addBusinessDays(new Date(), leadValue * 5), "yyyy-MM-dd");
      }
    }

    // Optimistically update UI
    setJobs(prev => prev.map(j => {
      if (j.id === jobId) {
        return {
          ...j,
          [field]: value,
          ...(smartDueDate ? { due_date: smartDueDate } : {}),
          job_data: {
            ...j.job_data,
            [field]: value,
            ...(smartDueDate ? { due_date: smartDueDate } : {}),
          }
        };
      }
      return j;
    }));

    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.patch(`${API}/jobs/${jobId}/production-status`, {
        [field]: value,
        ...(smartDueDate ? { due_date: smartDueDate } : {}),
      }, { headers });
      if (smartDueDate) {
        toast.success(`Due date set to ${smartDueDate}`);
      }
      // No toast for quick toggles - too noisy
    } catch (error) {
      console.error("Failed to update production status", error);
      toast.error("Failed to update status");
      // Revert on error
      loadJobs(search, statusFilter);
      return;
    }

    // Auto-move: when an Approved job has all 3 prep toggles ticked, offer to move to WIP (once)
    await maybePromptMoveToWip(jobBefore, field, value);
  };

  const maybePromptMoveToWip = async (jobBefore, field, value) => {
    if (!jobBefore || jobBefore.status !== "Approved") return;
    if (value !== true) return; // only when ticking ON
    const already = jobBefore.wip_prompt_dismissed ?? jobBefore.job_data?.wip_prompt_dismissed ?? false;
    if (already) return;

    const get = (f) => jobBefore[f] ?? jobBefore.job_data?.[f] ?? false;
    const merged = {
      garments_ordered: field === "garments_ordered" ? value : get("garments_ordered"),
      transfers_ordered: field === "transfers_ordered" ? value : get("transfers_ordered"),
      artwork_approved: field === "artwork_approved" ? value : get("artwork_approved"),
    };
    const allPrepped = merged.garments_ordered && merged.transfers_ordered && merged.artwork_approved;
    if (!allPrepped) return;

    const token = getToken();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const moveNow = window.confirm(
      `All items prepped — move "${jobBefore.job_name || "this job"}" to Work in Progress?`
    );

    if (moveNow) {
      setJobs(prev => prev.map(j => j.id === jobBefore.id ? { ...j, status: "In Production" } : j));
      try {
        await axios.put(`${API}/jobs/${jobBefore.id}`, {
          ...jobBefore.job_data,
          ...merged,
          status: "In Production",
        }, { headers: { ...headers, "X-Status-Source": "jobs_board_prompt" } });
        toast.success("Moved to Work in Progress");
      } catch (e) {
        toast.error("Failed to move job");
        loadJobs(search, statusFilter);
      }
    } else {
      // "Not yet" — remember dismissal so we don't nag again for this job
      setJobs(prev => prev.map(j => j.id === jobBefore.id
        ? { ...j, wip_prompt_dismissed: true, job_data: { ...j.job_data, wip_prompt_dismissed: true } }
        : j));
      try {
        await axios.patch(`${API}/jobs/${jobBefore.id}/production-status`, {
          wip_prompt_dismissed: true,
        }, { headers });
      } catch (e) {
        // non-critical
      }
    }
  };

  // Drag and drop handlers for board view
  const handleDragStart = (e, job) => {
    setDraggedJob(job);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragEnd = () => {
    setDraggedJob(null);
    setOverColumn(null);
  };

  const handleDragOver = (e, column) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setOverColumn(column.id);
  };

  const handleDrop = async (e, column) => {
    e.preventDefault();
    setOverColumn(null);
    
    if (!draggedJob) return;
    
    const newStatus = column.statuses[0];
    
    if (draggedJob.status === newStatus) {
      setDraggedJob(null);
      return;
    }
    
    // Optimistically update UI
    setJobs(prev => prev.map(j => 
      j.id === draggedJob.id ? { ...j, status: newStatus } : j
    ));
    
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(`${API}/jobs/${draggedJob.id}`, {
        ...draggedJob.job_data,
        status: newStatus,
      }, { headers: { ...headers, "X-Status-Source": "jobs_board_drag" } });
      toast.success(`Moved to ${column.title}`);
      // Print Sizing: prompt a completion-stage check when moving into Completed
      if (newStatus === "Complete") {
        // Purge original artwork files for this job (thumbnails are retained in job data)
        axios.post(`${API}/sizing/artwork/purge-job/${draggedJob.id}`, {}, { headers }).catch(() => {});
        const groups = (draggedJob.job_data?.artwork_groups || []).filter((g) => g.placement_name);
        const hasSizing = groups.some(
          (g) => (g.sizing_confirmed && g.print_width_mm && g.print_height_mm) || !g.sizing_confirmed
        );
        if (groups.length > 0 && hasSizing) {
          setCompletionCheckJob({ ...draggedJob, status: newStatus });
        }
      }
    } catch (error) {
      console.error("Failed to update job status", error);
      toast.error("Failed to update job status");
      loadJobs(search, statusFilter);
    }
    
    setDraggedJob(null);
  };

  // Filter jobs for board columns
  const getJobsForColumn = useCallback((column) => {
    let filtered = jobs.filter(job => 
      column.statuses.includes(job.status || "Draft") && job.status !== "Cancelled"
    );

    if (taskFilter === "only") {
      filtered = filtered.filter(job => job.job_data?.job_type === "task");
    } else if (taskFilter === "hide") {
      filtered = filtered.filter(job => job.job_data?.job_type !== "task");
    }
    
    if (methodFilter !== "All") {
      filtered = filtered.filter(job => {
        const methods = getProductionMethods(job);
        return methods.some(m => METHOD_BADGES[m]?.label === methodFilter);
      });
    }
    
    if (clientTypeFilter !== "All") {
      filtered = filtered.filter(job => 
        (job.job_data?.client_type || "Business") === clientTypeFilter
      );
    }
    
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(job => 
        (job.job_name || "").toLowerCase().includes(searchLower) ||
        (job.client_name || "").toLowerCase().includes(searchLower)
      );
    }
    
    return filtered;
  }, [jobs, methodFilter, clientTypeFilter, search, taskFilter]);

  // Filter jobs for list view
  const getFilteredJobs = useCallback(() => {
    // Exclude non-print task cards from the list view — they live on the board only
    let filtered = jobs.filter(j => j.job_data?.job_type !== "task");
    
    if (statusFilter !== "All") {
      if (statusFilter === "Sent") {
        // Mirror the "Sent / Awaiting Response" board column
        filtered = filtered.filter(j => j.status === "Sent" || j.status === "Changes Requested");
      } else {
        filtered = filtered.filter(j => j.status === statusFilter);
      }
    }
    
    if (clientTypeFilter !== "All") {
      filtered = filtered.filter(job => 
        (job.job_data?.client_type || "Business") === clientTypeFilter
      );
    }
    
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(job => 
        (job.job_name || "").toLowerCase().includes(searchLower) ||
        (job.client_name || "").toLowerCase().includes(searchLower) ||
        (job.client_email || "").toLowerCase().includes(searchLower)
      );
    }
    
    return filtered;
  }, [jobs, statusFilter, clientTypeFilter, search]);

  // Calculate total displayed jobs based on view mode and filters
  const totalDisplayed = viewMode === "board" 
    ? (boardFilter === "drafts" 
        ? jobs.filter(j => (j.status || "Draft") === "Draft").length
        : PRODUCTION_COLUMNS.reduce((sum, col) => sum + getJobsForColumn(col).length, 0) +
          getJobsForColumn(INITIAL_CONTACT_COLUMN).length +
          (boardFilter === "all" ? jobs.filter(j => (j.status || "Draft") === "Draft").length : 0))
    : getFilteredJobs().length;

  return (
    <WorkspaceLayout 
      title={`Jobs${showArchived ? ' — Archived' : ''}`} 
      actions={
        <div className="flex items-center gap-3">
          {/* View Toggle */}
          <div className="flex items-center gap-1 p-1 bg-workspace-bg rounded-md">
            <button
              onClick={() => { setViewMode("board"); setShowArchived(false); }}
              data-testid="view-board"
              className={`px-3 py-1.5 rounded text-sm font-medium transition-all ${
                viewMode === "board" && !showArchived
                  ? "bg-white shadow-sm text-workspace-text-primary" 
                  : "text-workspace-text-secondary hover:text-workspace-text-primary"
              }`}
            >
              Board
            </button>
            <button
              onClick={() => { setViewMode("list"); setShowArchived(false); }}
              data-testid="view-list"
              className={`px-3 py-1.5 rounded text-sm font-medium transition-all ${
                viewMode === "list" && !showArchived
                  ? "bg-white shadow-sm text-workspace-text-primary" 
                  : "text-workspace-text-secondary hover:text-workspace-text-primary"
              }`}
            >
              List
            </button>
            <button
              onClick={() => setShowArchived(true)}
              data-testid="view-archived"
              className={`px-3 py-1.5 rounded text-sm font-medium transition-all ${
                showArchived
                  ? "bg-white shadow-sm text-workspace-text-primary" 
                  : "text-workspace-text-secondary hover:text-workspace-text-primary"
              }`}
            >
              Archived {archivedTotal > 0 && `(${archivedTotal})`}
            </button>
          </div>
          
          <button
            onClick={() => showArchived ? loadArchivedJobs(archivedSearch) : loadJobs(search, statusFilter)}
            disabled={isLoading}
            className="p-2 rounded-md bg-white border border-workspace-border hover:bg-workspace-hover"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`} />
          </button>

          {viewMode === "board" && !showArchived && (
            <Button
              onClick={openTaskCreate}
              data-testid="add-task-btn"
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              size="sm"
            >
              <Plus className="w-4 h-4 mr-1.5" />
              Task
            </Button>
          )}
        </div>
      }
    >
      {/* Search and Filters Bar */}
      {!showArchived && (
        <div className="bg-white border-b border-workspace-border px-6 py-3">
          <form onSubmit={handleSearch} className="flex gap-3 items-center">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-workspace-text-muted" />
              <input
                placeholder="Search jobs..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="jobs-search-input"
                className="w-full pl-10 pr-4 py-2 bg-workspace-bg rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/30 border border-workspace-border"
              />
            </div>
            
            {viewMode === "list" && (
              <Select value={statusFilter} onValueChange={handleStatusFilter}>
                <SelectTrigger className="w-44 bg-white rounded-md border-workspace-border" data-testid="status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent className="rounded-md bg-white border border-workspace-border">
                  {JOB_STATUS_FILTERS.map((filter) => {
                    const c = statusCounts[filter.value];
                    return (
                      <SelectItem key={filter.value} value={filter.value} data-testid={`status-filter-option-${filter.value}`}>
                        {filter.label}{c !== undefined ? ` (${c})` : ""}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            )}
            
            {viewMode === "board" && (
              <>
                <Select value={boardFilter} onValueChange={setBoardFilter}>
                  <SelectTrigger className="w-36 bg-white rounded-md border-workspace-border" data-testid="board-filter">
                    <SelectValue placeholder="View" />
                  </SelectTrigger>
                  <SelectContent className="rounded-md bg-white border border-workspace-border">
                    {BOARD_FILTERS.map((filter) => (
                      <SelectItem key={filter.value} value={filter.value}>
                        {filter.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={methodFilter} onValueChange={setMethodFilter}>
                  <SelectTrigger className="w-36 bg-white rounded-md border-workspace-border" data-testid="method-filter">
                    <SelectValue placeholder="Method" />
                  </SelectTrigger>
                  <SelectContent className="rounded-md bg-white border border-workspace-border">
                    <SelectItem value="All">All Methods</SelectItem>
                    <SelectItem value="DTF">DTF</SelectItem>
                    <SelectItem value="HYBRID">HYBRID</SelectItem>
                    <SelectItem value="SCREEN">SCREEN</SelectItem>
                    <SelectItem value="EMB">EMB</SelectItem>
                    <SelectItem value="DTG">DTG</SelectItem>
                    <SelectItem value="CUSTOM">CUSTOM</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={clientTypeFilter} onValueChange={setClientTypeFilter}>
                  <SelectTrigger className="w-32 bg-white rounded-md border-workspace-border" data-testid="client-type-filter">
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent className="rounded-md bg-white border border-workspace-border">
                    <SelectItem value="All">All Types</SelectItem>
                    <SelectItem value="Retail">Retail</SelectItem>
                    <SelectItem value="Business">Business</SelectItem>
                    <SelectItem value="Promo">Promo</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={taskFilter} onValueChange={setTaskFilter}>
                  <SelectTrigger className="w-32 bg-white rounded-md border-workspace-border" data-testid="task-filter">
                    <SelectValue placeholder="Tasks" />
                  </SelectTrigger>
                  <SelectContent className="rounded-md bg-white border border-workspace-border">
                    <SelectItem value="all">All Cards</SelectItem>
                    <SelectItem value="only">Tasks Only</SelectItem>
                    <SelectItem value="hide">Hide Tasks</SelectItem>
                  </SelectContent>
                </Select>
              </>
            )}
            
            <span className="text-sm text-workspace-text-secondary">
              {totalDisplayed} of {total} jobs
            </span>
          </form>
        </div>
      )}

      {/* Main Content */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64 text-workspace-text-secondary">
          <RefreshCw className="w-6 h-6 animate-spin mr-2" />
          Loading jobs...
        </div>
      ) : showArchived ? (
        /* Archived View */
        <main className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="text-sm text-workspace-text-secondary">
              {archivedTotal} archived job{archivedTotal !== 1 ? "s" : ""}
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-workspace-text-muted" />
              <input
                placeholder="Search archived..."
                value={archivedSearch}
                onChange={(e) => {
                  setArchivedSearch(e.target.value);
                  loadArchivedJobs(e.target.value);
                }}
                data-testid="archived-search-input"
                className="w-64 pl-10 pr-4 py-2 bg-workspace-bg rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/30 border border-workspace-border"
              />
            </div>
          </div>

          {archivedJobs.length === 0 ? (
            <div className="text-center py-16">
              <Archive className="w-12 h-12 mx-auto mb-4 text-workspace-text-muted" />
              <p className="text-lg font-medium text-workspace-text-primary">No archived jobs</p>
              <p className="text-sm text-workspace-text-secondary mt-1">
                Completed jobs are automatically archived after 48 hours
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-workspace-border overflow-hidden">
              <table className="w-full">
                <thead className="bg-workspace-bg border-b border-workspace-border">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-workspace-text-secondary uppercase tracking-wider">Job</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-workspace-text-secondary uppercase tracking-wider">Client</th>
                    <th className="px-4 py-3 text-left text-aa-xs font-semibold text-gray-600 uppercase tracking-wider">Completed</th>
                    <th className="px-4 py-3 text-left text-aa-xs font-semibold text-gray-600 uppercase tracking-wider">Archived</th>
                    <th className="px-4 py-3 text-right text-aa-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {archivedJobs.map((job) => (
                    <tr key={job.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-4">
                        <div className="flex flex-col">
                          <span className="text-aa-xs font-mono text-gray-400">{job.job_number}</span>
                          <span className="text-aa-sm font-medium text-gray-800">{job.job_name || "Untitled"}</span>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-aa-sm text-gray-600">{job.client_name || "—"}</span>
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-aa-sm text-gray-600">
                          {job.completed_at ? formatDate(job.completed_at) : formatDate(job.updated_at)}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-aa-sm text-gray-600">
                          {job.archived_at ? formatDate(job.archived_at) : "—"}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <button
                          onClick={() => handleRestore(job.id)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-aa-xs font-medium text-aa-primary hover:bg-aa-primary/10 rounded-aa-sm transition-all"
                          data-testid={`restore-job-${job.id}`}
                        >
                          <ArchiveRestore className="w-3.5 h-3.5" />
                          Restore
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </main>
      ) : viewMode === "board" ? (
        /* Board View - Full Width */
        <main className="w-full px-6 pt-6 pb-3 flex flex-col overflow-hidden h-[calc(100vh-120px)]">
          <div className="flex gap-4 overflow-x-auto overflow-y-hidden pb-3 flex-1 min-h-0">
            {/* Initial Contact Column - pre-quote holding zone, always visible except Drafts-only */}
            {boardFilter !== "drafts" && (
              <BoardColumn
                column={INITIAL_CONTACT_COLUMN}
                jobs={getJobsForColumn(INITIAL_CONTACT_COLUMN)}
                onDragOver={(e) => handleDragOver(e, INITIAL_CONTACT_COLUMN)}
                onDrop={handleDrop}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onCardClick={openJob}
                onTaskClick={openTaskEdit}
                defaultTaskRate={defaultLabourRate}
                onDuplicate={handleDuplicateJob}
                onProductionSheet={handleProductionSheet}
                onProductionStatusChange={handleProductionStatusChange}
                onArchive={handleArchive}
                isOver={overColumn === INITIAL_CONTACT_COLUMN.id}
                onAddEnquiry={openEnquiryCreate}
              />
            )}

            {/* Draft Column - Only shown when boardFilter is 'all' or 'drafts' */}
            {(boardFilter === "all" || boardFilter === "drafts") && (
              <div 
                className="flex-shrink-0 w-72 bg-slate-100 rounded-aa-lg p-4 border-2 border-dashed border-slate-300 flex flex-col h-full"
                data-testid="draft-column"
              >
                <div className="flex items-center justify-between mb-3 flex-shrink-0">
                  <h3 className="font-semibold text-slate-600 flex items-center gap-2">
                    <StickyNote className="w-4 h-4" />
                    Drafts
                  </h3>
                  <span className="text-aa-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full">
                    {jobs.filter(j => (j.status || "Draft") === "Draft").length}
                  </span>
                </div>
                <div className="space-y-2 flex-1 overflow-y-auto min-h-0 -mr-2 pr-2">
                  {jobs
                    .filter(j => (j.status || "Draft") === "Draft")
                    .map((job) => (
                      <DraftJobCard
                        key={job.id}
                        job={job}
                        onClick={openJob}
                      />
                    ))}
                  {jobs.filter(j => (j.status || "Draft") === "Draft").length === 0 && (
                    <p className="text-center text-aa-xs text-slate-400 py-4">No drafts</p>
                  )}
                </div>
              </div>
            )}
            
            {/* Production Columns - Hidden when boardFilter is 'drafts' */}
            {boardFilter !== "drafts" && PRODUCTION_COLUMNS.map((column) => (
              <BoardColumn
                key={column.id}
                column={column}
                jobs={getJobsForColumn(column)}
                onDragOver={(e) => handleDragOver(e, column)}
                onDrop={handleDrop}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onCardClick={openJob}
                onTaskClick={openTaskEdit}
                defaultTaskRate={defaultLabourRate}
                onDuplicate={handleDuplicateJob}
                onProductionSheet={handleProductionSheet}
                onProductionStatusChange={handleProductionStatusChange}
                onArchive={handleArchive}
                isOver={overColumn === column.id}
              />
            ))}
          </div>
        </main>
      ) : (
        /* List View - Full Width */
        <main className="p-6">
          {getFilteredJobs().length === 0 ? (
            <div className="text-center py-12">
              <FolderOpen className="w-12 h-12 mx-auto mb-4 text-workspace-text-muted" />
              <p className="text-lg font-medium text-workspace-text-primary">No jobs found</p>
              <p className="text-sm text-workspace-text-secondary mt-1">
                {search ? "Try a different search term" : "Create a quote to see it here"}
              </p>
            </div>
          ) : (
            <div className="grid gap-3">
              {getFilteredJobs().map((job) => (
                <JobListRow
                  key={job.id}
                  job={job}
                  onEdit={openJob}
                  onDuplicate={handleDuplicateJob}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )}
        </main>
      )}

      {/* Non-print Task create/edit modal */}
      <Dialog open={showTaskModal} onOpenChange={setShowTaskModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ListTodo className="w-5 h-5 text-indigo-600" />
              {editingTaskId ? "Edit Task" : "New Task"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="task-name">Task Name <span className="text-red-500">*</span></Label>
              <Input
                id="task-name"
                placeholder="e.g., Logo redesign for ABC Corp"
                value={taskForm.job_name}
                onChange={(e) => setTaskForm({ ...taskForm, job_name: e.target.value })}
                data-testid="task-name-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Customer</Label>
              <TaskCustomerField form={taskForm} setForm={setTaskForm} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="task-due">Due Date</Label>
                <Input
                  id="task-due"
                  type="date"
                  value={taskForm.due_date}
                  onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })}
                  data-testid="task-due-input"
                />
              </div>
            <div className="space-y-1.5">
              <Label htmlFor="task-column">Start in column</Label>
              <Select value={taskForm.status} onValueChange={(v) => setTaskForm({ ...taskForm, status: v })}>
                <SelectTrigger id="task-column" data-testid="task-column-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Sent">Sent / Awaiting Response</SelectItem>
                  <SelectItem value="Approved">Approved &amp; Ready</SelectItem>
                  <SelectItem value="In Production">Work in Progress</SelectItem>
                  <SelectItem value="Complete">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            </div>
            <div className="space-y-1.5">
              <Label>Link to existing job</Label>
              <TaskJobLinkField
                value={taskForm.linked_job}
                onChange={(link) => setTaskForm((f) => ({ ...f, linked_job: link }))}
                excludeId={editingTaskId}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="task-notes">Notes</Label>
              <Textarea
                id="task-notes"
                placeholder="Optional details about this task..."
                value={taskForm.notes}
                onChange={(e) => setTaskForm({ ...taskForm, notes: e.target.value })}
                rows={3}
                data-testid="task-notes-input"
              />
            </div>
            <TaskFinancials
              form={taskForm}
              setForm={setTaskForm}
              defaultRate={defaultLabourRate}
              draft={taskTimeDraft}
              setDraft={setTaskTimeDraft}
              onError={(msg) => toast.error(msg)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTaskModal(false)}>Cancel</Button>
            <Button
              onClick={saveTask}
              disabled={isSavingTask}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="task-save-btn"
            >
              {isSavingTask ? "Saving..." : editingTaskId ? "Update Task" : "Create Task"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Enquiry (Initial Contact lead) create modal */}
      <Dialog open={showEnquiryModal} onOpenChange={setShowEnquiryModal}>
        <DialogContent className="max-w-md" data-testid="enquiry-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-sky-600" />
              New Enquiry
            </DialogTitle>
            <DialogDescription>
              Log a new lead into the Initial Contact column before quoting.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="enquiry-name">Enquiry / What they want</Label>
              <Input
                id="enquiry-name"
                placeholder="e.g., 30 hoodies for footy club"
                value={enquiryForm.job_name}
                onChange={(e) => setEnquiryForm({ ...enquiryForm, job_name: e.target.value })}
                data-testid="enquiry-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="enquiry-business">Business Name</Label>
                <Input
                  id="enquiry-business"
                  placeholder="Optional"
                  value={enquiryForm.business_name}
                  onChange={(e) => setEnquiryForm({ ...enquiryForm, business_name: e.target.value })}
                  data-testid="enquiry-business-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="enquiry-contact">Contact Name</Label>
                <Input
                  id="enquiry-contact"
                  placeholder="Optional"
                  value={enquiryForm.contact_name}
                  onChange={(e) => setEnquiryForm({ ...enquiryForm, contact_name: e.target.value })}
                  data-testid="enquiry-contact-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="enquiry-email">Email</Label>
                <Input
                  id="enquiry-email"
                  type="email"
                  placeholder="Optional"
                  value={enquiryForm.email}
                  onChange={(e) => setEnquiryForm({ ...enquiryForm, email: e.target.value })}
                  data-testid="enquiry-email-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="enquiry-phone">Phone</Label>
                <Input
                  id="enquiry-phone"
                  placeholder="Optional"
                  value={enquiryForm.phone}
                  onChange={(e) => setEnquiryForm({ ...enquiryForm, phone: e.target.value })}
                  data-testid="enquiry-phone-input"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="enquiry-notes">Notes</Label>
              <Textarea
                id="enquiry-notes"
                placeholder="How they got in touch, what they asked for..."
                value={enquiryForm.notes}
                onChange={(e) => setEnquiryForm({ ...enquiryForm, notes: e.target.value })}
                rows={3}
                data-testid="enquiry-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEnquiryModal(false)}>Cancel</Button>
            <Button
              onClick={saveEnquiry}
              disabled={isSavingEnquiry}
              className="bg-sky-600 hover:bg-sky-700 text-white"
              data-testid="enquiry-save-btn"
            >
              {isSavingEnquiry ? "Saving..." : "Log Enquiry"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Print Sizing: completion-stage check when a job moves to Completed */}
      {completionCheckJob && (
        <CompletionSizingCheckModal
          open={!!completionCheckJob}
          job={completionCheckJob}
          onClose={() => setCompletionCheckJob(null)}
        />
      )}
    </WorkspaceLayout>
  );
}
